import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Manages all state and side-effects for QR deep-link "highlight scanned piece" behaviour.
 *
 * Encapsulates:
 *  - scanned-piece locator UI state (found, group index, snapshot, dismissed)
 *  - browser scroll-restoration override so the page lands at the top, not mid-scroll
 *  - scroll-reset logic that keeps the page at the top until the operator taps Jump
 *  - an imperative ref the child GroupedPrints uses to expose its jump-and-scroll action
 *
 * @param {string|null} scannedPieceUuid - UUID from the URL `?scannedPiece=` param.
 */
export default function useScannedPieceDeepLink(scannedPieceUuid) {
  const [hasScannedPieceInRun, setHasScannedPieceInRun] = useState(false);
  const [scannedPieceGroupIndex, setScannedPieceGroupIndex] = useState(null);
  const [scannedPieceSnapshotUrl, setScannedPieceSnapshotUrl] = useState(null);
  const [hasDismissedScannedPieceLocator, setHasDismissedScannedPieceLocator] = useState(false);
  const [totalGroups, setTotalGroups] = useState(0);

  // Imperative handle written by GroupedPrints; called when operator taps Jump.
  const jumpToScannedPieceRef = useRef(null);
  // Prevents the scroll-reset from firing once the operator has explicitly jumped.
  const hasRequestedScannedPieceJumpRef = useRef(false);
  // One-shot guard: only run the scroll reset once per unique piece+group combination.
  const initialScrollResetKeyRef = useRef(null);

  // Reset all locator state when the scanned piece changes (new QR scan).
  useEffect(() => {
    setHasDismissedScannedPieceLocator(false);
    hasRequestedScannedPieceJumpRef.current = false;
    initialScrollResetKeyRef.current = null;
  }, [scannedPieceUuid]);

  // Disable browser scroll restoration so it doesn't fight our top-of-page reset.
  useEffect(() => {
    if (!scannedPieceUuid || !('scrollRestoration' in window.history)) return undefined;
    const prev = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    return () => {
      window.history.scrollRestoration = prev;
    };
  }, [scannedPieceUuid]);

  // QR deep links should land at the top of the run page, not jump to the piece,
  // until the operator explicitly taps Jump. Multiple timers beat browsers that
  // re-apply scroll position asynchronously after the initial paint.
  useEffect(() => {
    if (!scannedPieceUuid || !hasScannedPieceInRun || scannedPieceGroupIndex === null)
      return undefined;

    const resetKey = `${scannedPieceUuid}:${scannedPieceGroupIndex}`;
    if (initialScrollResetKeyRef.current === resetKey) return undefined;
    initialScrollResetKeyRef.current = resetKey;

    const resetScroll = () => {
      if (!hasRequestedScannedPieceJumpRef.current) {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      }
    };

    resetScroll();
    const pendingFrame = requestAnimationFrame(resetScroll);
    const timers = [0, 120, 360].map(delay => setTimeout(resetScroll, delay));

    return () => {
      cancelAnimationFrame(pendingFrame);
      timers.forEach(clearTimeout);
    };
  }, [scannedPieceUuid, hasScannedPieceInRun, scannedPieceGroupIndex]);

  // Receives group info from GroupedPrints and distributes it into local state.
  const onGroupsInfoChange = useCallback(
    ({
      totalGroups: total,
      hasScannedPieceInRun: hasIt,
      scannedPieceGroupIndex: scannedGroupIndex,
      scannedPieceSnapshotUrl: snapshotUrl,
    }) => {
      setTotalGroups(total);
      setHasScannedPieceInRun(Boolean(hasIt));
      setScannedPieceGroupIndex(scannedGroupIndex);
      setScannedPieceSnapshotUrl(snapshotUrl || null);
    },
    []
  );

  const onJump = useCallback(() => {
    hasRequestedScannedPieceJumpRef.current = true;
    if (jumpToScannedPieceRef.current) {
      jumpToScannedPieceRef.current();
    }
  }, []);

  return {
    hasScannedPieceInRun,
    scannedPieceGroupIndex,
    scannedPieceSnapshotUrl,
    hasDismissedScannedPieceLocator,
    setHasDismissedScannedPieceLocator,
    totalGroups,
    jumpToScannedPieceRef,
    onGroupsInfoChange,
    onJump,
  };
}
